import { useEffect, useState } from 'react';
import { announcementAPI } from '@/lib/api';

/**
 * useAnnouncements — dashboard-scoped hook for fetching active announcements.
 *
 * Consolidates the identical loader block that lived inside every role
 * dashboard after Phase 20.2 (KR / Agency / Agent / Accounting). Returns
 * only the active announcements, capped at `limit`, matching the exact
 * pre-cleanup behavior.
 *
 * @param {number} limit  Max announcements returned. Default 5.
 * @returns {{ announcements: Array, loading: boolean, error: Error|null }}
 */
export const useAnnouncements = (limit = 5) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await announcementAPI.list();
        if (cancelled) return;
        setAnnouncements(res.data.filter((a) => a.active).slice(0, limit));
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load announcements:', e);
        setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  return { announcements, loading, error };
};

export default useAnnouncements;
